import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  FiFileText, FiAlertCircle, FiTrash2, FiSearch, FiChevronLeft,
  FiCheck, FiPlus, FiX, FiUser, FiChevronRight, FiChevronDown,
  FiArrowLeft, FiArrowRight,
} from 'react-icons/fi';
import '../commissionscan/CommissionScan.css';
import '../../styles/QuoteProposal.css';



const getToolboxToken = () => localStorage.getItem('toolboxToken') || '';
const getNcToken      = () => localStorage.getItem('amsToken')     || '';

const ACCEPTED_TYPES = '.pdf,.jpg,.jpeg';
const ACCEPTED_MIME  = ['application/pdf', 'image/jpeg', 'image/jpg'];

/* ── NowCerts billing type options ── */
const BILLING_TYPE_DATA = [
  { name: 'Initial_Base_Premium',           value: 0   },
  { name: 'Positive_Endorsement',           value: 1   },
  { name: 'Negative_Endorsement',           value: 2   },
  { name: 'Cancel',                         value: 5   },
  { name: 'Renew',                          value: 6   },
  { name: 'Reinstate',                      value: 7   },
  { name: 'Govt_Discount',                  value: 9   },
  { name: 'Premium_Audit',                  value: 13  },
  { name: 'General_Discount',               value: 14  },
  { name: 'Credit',                         value: 15  },
  { name: 'Installment',                    value: 200 },
  { name: 'New_Policy_Premium',             value: 210 },
  { name: 'No_Premium_Impact',              value: 230 },
  { name: 'Fees',                           value: 3   },
  { name: 'Agency_Fee',                     value: 10  },
  { name: 'Risk_Management_Fee',            value: 101 },
  { name: 'Carrier_Fee',                    value: 11  },
  { name: 'Policy_Fee',                     value: 111 },
  { name: 'MGA_Fee',                        value: 12  },
  { name: 'Inspection_Fee',                 value: 20  },
  { name: 'Al_Endorsements_Fee',            value: 21  },
  { name: 'Subscription_Fee',               value: 16  },
  { name: 'Program_Fee',                    value: 19  },
  { name: 'Taxes',                          value: 4   },
  { name: 'State_Tax',                      value: 17  },
  { name: 'Surplus_Lines_Tax',              value: 22  },
  { name: 'Miscellaneous_Agency_Revenue',   value: 23  },
  { name: 'Other_Tax',                      value: 18  },
  { name: 'Fire_Marshall_Tax',              value: 181 },
  { name: 'Processing_Fee',                 value: 35  },
  { name: 'Filing_Fee',                     value: 351 },
  { name: 'APTC_Tax_Credit',                value: 45  },
  { name: 'Expense_Constant',               value: 100 },
  { name: 'Surplus_Contribution',           value: 150 },
  { name: 'AB_TPA_Fee',                     value: 160 },
  { name: 'Independent_Agent_Broker_Fee',   value: 170 },
  { name: 'Payment_Processing_Fee',         value: 180 },
  { name: 'Collateral_Fee',                 value: 190 },
  { name: 'MVR_Fee',                        value: 240 },
  { name: 'CC_Fee',                         value: 250 },
  { name: 'Admin_Fee',                      value: 350 },
  { name: 'Other',                          value: 8   },
];

/* ── AMS file types ── */
interface AmsFileItem {
  id: string;
  name: string;
  type: 'folder' | 'file';
  description?: string;
  size?: string;
  uploader?: string;
  created?: string;
  children?: AmsFileItem[];
}

interface AmsFileSelection {
  file: AmsFileItem;
  insuredId: string;
  folderId: string;
}

/* ── AMS File Browser (pre-loaded for already-selected client) ── */
const AmsFileBrowser: React.FC<{
  insuredId: string;
  clientName: string;
  onFileSelected: (sel: AmsFileSelection | null) => void;
}> = ({ insuredId, clientName, onFileSelected }) => {
  const [rootFolder,      setRootFolder]      = useState<AmsFileItem | null>(null);
  const [currentFolder,   setCurrentFolder]   = useState<AmsFileItem | null>(null);
  const [tableData,       setTableData]       = useState<AmsFileItem[]>([]);
  const [tableLoading,    setTableLoading]    = useState(false);
  const [fetchError,      setFetchError]      = useState('');
  const [expandedFolders, setExpandedFolders] = useState<Record<string, boolean>>({});
  const [selectedFile,    setSelectedFile]    = useState<AmsFileItem | null>(null);
  const [folderHistory,   setFolderHistory]   = useState<AmsFileItem[]>([]);
  const [historyIndex,    setHistoryIndex]    = useState(-1);

  const mapFiles = (rawFiles: any[], parentId: string): AmsFileItem[] =>
    (rawFiles || []).map((f: any) => ({
      id:             f.databaseId,
      name:           f.name,
      type:           f.fileOrFolder === 'Folder' ? 'folder' : 'file',
      description:    f.fileOrFolder,
      size:           f.size?.toString() || '-',
      uploader:       f.creatorName || '-',
      created:        f.createDate  || '-',
      children:       f.fileOrFolder === 'Folder' ? [] : undefined,
    }));

  const fetchFiles = useCallback(async (folderId?: string) => {
    const url = `${(process.env.REACT_APP_NOWCERTSAPI         || '')}/api/nowcert/getInsuredFilesList?insuredId=${insuredId}`
      + `&nctoken=${getNcToken()}`
      + `&folderId=${folderId || ''}`;
    const res = await axios.get(url, { headers: { Authorization: `Bearer ${getToolboxToken()}` } });
    return res.data;
  }, [insuredId]);

  useEffect(() => {
    setFetchError('');
    setTableLoading(true);
    fetchFiles()
      .then(data => {
        const fd = data?.data || data;
        const root: AmsFileItem = {
          id:       fd.currentFolderId || insuredId,
          name:     fd.name || clientName || 'Root',
          type:     'folder',
          children: mapFiles(fd.files || [], fd.currentFolderId || insuredId),
        };
        setRootFolder(root);
        setCurrentFolder(root);
        setTableData(root.children || []);
        setExpandedFolders({ [root.id]: true });
        setFolderHistory([root]);
        setHistoryIndex(0);
      })
      .catch(() => setFetchError('Unable to load files. Please try again.'))
      .finally(() => setTableLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuredId]);

  const openFolder = useCallback((folder: AmsFileItem, childrenOverride?: AmsFileItem[]) => {
    const children = childrenOverride ?? folder.children ?? [];
    const node: AmsFileItem = { ...folder, children };
    setCurrentFolder(node);
    setTableData(children);
    setExpandedFolders(p => ({ ...p, [folder.id]: true }));
    setFolderHistory(prev => {
      const base = historyIndex >= 0 ? prev.slice(0, historyIndex + 1) : [];
      const exists = base.findIndex(f => f.id === folder.id);
      if (exists !== -1) { setHistoryIndex(exists); return base; }
      const next = [...base, node];
      setHistoryIndex(next.length - 1);
      return next;
    });
  }, [historyIndex]);

  const updateChildrenInTree = (root: AmsFileItem, targetId: string, newChildren: AmsFileItem[]): AmsFileItem => {
    if (root.id === targetId) return { ...root, children: newChildren };
    if (root.children) return { ...root, children: root.children.map(c => updateChildrenInTree(c, targetId, newChildren)) };
    return root;
  };

  const toggleFolder = useCallback((item: AmsFileItem) => {
    if (expandedFolders[item.id]) {
      setExpandedFolders(p => ({ ...p, [item.id]: false }));
      return;
    }
    if (item.children && item.children.length === 0) {
      setTableLoading(true);
      fetchFiles(item.id)
        .then(data => {
          const newChildren = mapFiles(data?.data?.files || [], item.id);
          setRootFolder(prev => prev ? updateChildrenInTree(prev, item.id, newChildren) : prev);
          openFolder(item, newChildren);
        })
        .catch(() => {})
        .finally(() => setTableLoading(false));
    } else if (item.type === 'folder') {
      openFolder(item);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedFolders, fetchFiles, openFolder]);

  const canGoBack    = historyIndex > 0;
  const canGoForward = historyIndex >= 0 && historyIndex < folderHistory.length - 1;

  const handleBack = () => {
    const newIdx = historyIndex - 1;
    const f = folderHistory[newIdx];
    if (!f) return;
    setHistoryIndex(newIdx); setCurrentFolder(f); setTableData(f.children || []);
    setExpandedFolders(p => ({ ...p, [f.id]: true }));
  };

  const handleForward = () => {
    const newIdx = historyIndex + 1;
    const f = folderHistory[newIdx];
    if (!f) return;
    setHistoryIndex(newIdx); setCurrentFolder(f); setTableData(f.children || []);
    setExpandedFolders(p => ({ ...p, [f.id]: true }));
  };

  const buildPath = (root: AmsFileItem, targetId: string): AmsFileItem[] => {
    const path: AmsFileItem[] = [];
    const dfs = (node: AmsFileItem, acc: AmsFileItem[]): boolean => {
      const next = [...acc, node];
      if (node.id === targetId) { path.push(...next); return true; }
      for (const c of node.children || []) if (dfs(c, next)) return true;
      return false;
    };
    dfs(root, []);
    return path.length ? path : [root];
  };

  const fmtSize = (size?: string) => {
    if (!size || size === '-') return '—';
    const n = parseInt(size, 10);
    if (isNaN(n)) return size;
    return n >= 1048576 ? (n / 1048576).toFixed(2) + ' MB' : (n / 1024).toFixed(2) + ' KB';
  };

  const renderTreeItem = (item: AmsFileItem, level = 0): React.ReactNode => {
    const expanded = expandedFolders[item.id];
    return (
      <div key={item.id}>
        <div
          className={`cs-ams-tree-row${level === 0 ? ' cs-ams-tree-row--root' : ''}`}
          style={{ paddingLeft: level * 16 + 10 }}
          onClick={() => toggleFolder(item)}
        >
          {expanded ? <FiChevronDown size={11} className="cs-ams-chevron" /> : <FiChevronRight size={11} className="cs-ams-chevron" />}
          <span className="cs-ams-tree-name" title={item.name}>{item.name}</span>
        </div>
        {expanded && (item.children || []).filter(c => c.type === 'folder').map(c => renderTreeItem(c, level + 1))}
      </div>
    );
  };

  if (tableLoading && !rootFolder) {
    return (
      <div className="cs-ams-state" style={{ padding: '32px 0' }}>
        <span className="cs-ams-spinner" />
        <span>Loading files…</span>
      </div>
    );
  }

  if (fetchError) return <div className="cs-ams-error-msg">{fetchError}</div>;

  return (
    <div className="cs-client-panel cs-client-panel--browser">
      <div className="cs-ams-wrap">
        <div className="cs-ams-client-bar">
          <FiUser size={13} className="cs-ams-client-bar__icon" />
          <span className="cs-ams-client-bar__name">{clientName}</span>
        </div>

        {rootFolder && (
          <>
            {selectedFile && (
              <div className="cs-ams-file-card">
                <div className="cs-ams-file-card__left">
                  <FiFileText size={16} className="cs-ams-file-card__icon" />
                  <div>
                    <p className="cs-ams-file-card__name" title={selectedFile.name}>{selectedFile.name}</p>
                    {selectedFile.size && selectedFile.size !== '-' && (
                      <p className="cs-ams-file-card__size">{fmtSize(selectedFile.size)}</p>
                    )}
                  </div>
                </div>
                <button
                  className="cs-ams-file-card__clear"
                  onClick={() => { setSelectedFile(null); onFileSelected(null); }}
                >
                  <FiX size={13} />
                </button>
              </div>
            )}

            <div className="cs-ams-breadcrumb">
              <button className="cs-ams-nav-btn" disabled={!canGoBack}    onClick={handleBack}    title="Back"><FiArrowLeft  size={13} /></button>
              <button className="cs-ams-nav-btn" disabled={!canGoForward} onClick={handleForward} title="Forward"><FiArrowRight size={13} /></button>
              <div className="cs-ams-breadcrumb__path">
                {buildPath(rootFolder, currentFolder?.id || rootFolder.id).map((seg, i) => (
                  <React.Fragment key={seg.id}>
                    {i > 0 && <span className="cs-ams-breadcrumb__sep">/</span>}
                    <span className="cs-ams-breadcrumb__seg" title={seg.name}>{seg.name}</span>
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="cs-ams-layout">
              <div className="cs-ams-tree">{renderTreeItem(rootFolder)}</div>
              <div className="cs-ams-table-wrap">
                {tableLoading ? (
                  <div className="cs-ams-state"><span className="cs-ams-spinner" /><span>Loading…</span></div>
                ) : tableData.length === 0 ? (
                  <div className="cs-ams-state cs-ams-state--empty">No files in this folder.</div>
                ) : (
                  <table className="cs-ams-table">
                    <thead>
                      <tr>
                        <th className="cs-ams-th">Name</th>
                        <th className="cs-ams-th">Type</th>
                        <th className="cs-ams-th">Size</th>
                        <th className="cs-ams-th">Uploader</th>
                        <th className="cs-ams-th">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map(item => {
                        const isSelected = selectedFile?.id === item.id;
                        return (
                          <tr
                            key={item.id}
                            className={`cs-ams-tr${item.type === 'folder' ? ' cs-ams-tr--folder' : ' cs-ams-tr--file'}${isSelected ? ' cs-ams-tr--active' : ''}`}
                            onClick={() => {
                              if (item.type === 'folder') { toggleFolder(item); return; }
                              setSelectedFile(item);
                              onFileSelected({ file: item, insuredId, folderId: currentFolder?.id || '' });
                            }}
                          >
                            <td className="cs-ams-td cs-ams-td--name"><span title={item.name}>{item.name}</span></td>
                            <td className="cs-ams-td">{item.description || '—'}</td>
                            <td className="cs-ams-td">{fmtSize(item.size)}</td>
                            <td className="cs-ams-td">{item.uploader || '—'}</td>
                            <td className="cs-ams-td">{item.created || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ── Types ── */
interface Client { name: string; databaseId: string; eMail?: string; }
interface Policy {
  id: string;
  policyNumber: string;
  carrierName?: string;
  linesOfBusiness?: string[];
  status?: string;
  effectiveDate?: string;
  expirationDate?: string;
  totalPremium?: number;
  isQuote?: boolean;
}

export interface LineItem {
  lineCode: string;
  transactionCodeBeforeMapped: string;
  transactionCodeAfterMapped: string;
  effectiveDate: string;
  grossAmount: string;
  brokerCommission: string;
  lineAmount: string;
}

export interface InvoiceData {
  invoiceNumber: string;
  invoiceDate: string;
  invoiceDueDate: string;
  billedToName: string;
  billedToAddressLine: string;
  billedToCity: string;
  billedToState: string;
  billedToZipCode: string;
  soldToName: string;
  soldToAddressLine: string;
  soldToCity: string;
  soldToState: string;
  soldToZipCode: string;
  paymentAddressLine: string;
  paymentCity: string;
  paymentState: string;
  paymentZipCode: string;
  producer: string;
  bookLocation: string;
  insuredName: string;
  policyNumber: string;
  insuranceCompany: string;
  totalAmountDue: string;
  totalGrossAmount: string;
  totalBrokerCommission: string;
  invoiceLineItems: LineItem[];
}

interface Props { onGoToHistory?: () => void; }

const InvoiceScanUpload: React.FC<Props> = ({ onGoToHistory: _onGoToHistory }) => {
  type Step = 'client-select' | 'upload' | 'review';
  const [step, setStep] = useState<Step>('client-select');

  /* Client search */
  const [clientQuery,    setClientQuery]    = useState('');
  const [clientResults,  setClientResults]  = useState<Client[]>([]);
  const [clientLoading,  setClientLoading]  = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  /* Policy */
  const [policyList,     setPolicyList]     = useState<Policy[]>([]);
  const [policyLoading,  setPolicyLoading]  = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);

  /* Upload */
  const [uploadTab,        setUploadTab]        = useState<'local' | 'ams'>('local');
  const [file,             setFile]             = useState<File | null>(null);
  const [selectedAmsFile,  setSelectedAmsFile]  = useState<AmsFileSelection | null>(null);
  const [isDragging,       setIsDragging]       = useState(false);
  const [uploadLoading,    setUploadLoading]    = useState(false);
  const [uploadError,      setUploadError]      = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* Balance */
  const [balance,             setBalance]             = useState(0);
  const [balanceLoading,      setBalanceLoading]      = useState(false);
  const [insufficientBalance, setInsufficientBalance] = useState(false);

  /* Review — extracted invoice data */
  const [invoiceData,          setInvoiceData]          = useState<InvoiceData | null>(null);
  const [lineItems,            setLineItems]            = useState<LineItem[]>([]);
  const [transactionId,        setTransactionId]        = useState('');
  const [fileUrl,              setFileUrl]              = useState('');
  const [isUpdateInNowcerts,   setIsUpdateInNowcerts]   = useState(false);
  const [selectedBillingType,  setSelectedBillingType]  = useState('');
  const [billingTypeAmount,    setBillingTypeAmount]    = useState('');
  const [billingDropdownOpen,  setBillingDropdownOpen]  = useState(false);
  const billingDropdownRef = useRef<HTMLDivElement>(null);
  const [lineItemsPage,        setLineItemsPage]        = useState(1);
  const [fieldErrors,          setFieldErrors]          = useState<{ totalAmountDue?: string; totalGrossAmount?: string; billingTypeAmount?: string; selectedBillingType?: string }>({});

  /* Endorsement fee state */
  const [feeLoading, setFeeLoading] = useState(false);
  const [feeSuccess, setFeeSuccess] = useState(false);
  const [feeError,   setFeeError]   = useState('');

  /* Billing dropdown click-outside */
  useEffect(() => {
    if (!billingDropdownOpen) return;
    const handle = (e: MouseEvent) => {
      if (billingDropdownRef.current && !billingDropdownRef.current.contains(e.target as Node))
        setBillingDropdownOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [billingDropdownOpen]);

  /* Balance fetch */
  useEffect(() => {
    if (!(process.env.REACT_APP_STRIPEPAYMENTGATEWAY || '')) return;
    setBalanceLoading(true);
    axios.get(`${(process.env.REACT_APP_STRIPEPAYMENTGATEWAY || '')}/api/billing/current-balance`, {
      headers: { Authorization: `Bearer ${getToolboxToken()}` },
    })
      .then(res => {
        if (res.data.success) {
          const amt = res.data.currentBalance / 100;
          setBalance(amt);
          setInsufficientBalance(amt < 0.25);
        }
      })
      .finally(() => setBalanceLoading(false));
  }, []);

  /* Client search debounce */
  useEffect(() => {
    if (!clientQuery.trim()) { setClientResults([]); return; }
    const t = setTimeout(() => {
      setClientLoading(true);
      axios.get(`${(process.env.REACT_APP_NOWCERTSAPI         || '')}/api/nowcert/customerlist`, {
        params: { searchText: clientQuery, nowCertsToken: getNcToken() },
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      })
        .then(r => setClientResults(r.data || []))
        .finally(() => setClientLoading(false));
    }, 400);
    return () => clearTimeout(t);
  }, [clientQuery]);

  /* Load policies when client selected */
  const loadPolicies = useCallback(async (insuredId: string) => {
    setPolicyLoading(true);
    setPolicyList([]);
    setSelectedPolicy(null);
    try {
      const res = await axios.get(`${(process.env.REACT_APP_NOWCERTSAPI         || '')}/api/nowcert/insuredpolicies`, {
        params: { insuredId, nowCertsToken: getNcToken() },
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      });
      setPolicyList(
        Array.isArray(res.data)
          ? res.data.map((p: any) => ({
              id:              p.databaseId,
              policyNumber:    p.policyNumber,
              carrierName:     p.carrierName,
              linesOfBusiness: p.linesOfBusiness || [],
              status:          p.status,
              effectiveDate:   p.effectiveDate,
              expirationDate:  p.expirationDate,
              totalPremium:    p.totalPremium,
              isQuote:         p.isQuote,
            }))
          : [],
      );
    } catch { /* silent */ }
    finally { setPolicyLoading(false); }
  }, []);

  const selectClient = useCallback((client: Client) => {
    setSelectedClient(client);
    setClientQuery('');
    setClientResults([]);
    loadPolicies(client.databaseId);
  }, [loadPolicies]);

  /* File handling */
  const pickFile = useCallback((incoming: FileList | File[]) => {
    const first = Array.from(incoming).find(
      f => ACCEPTED_MIME.includes(f.type) || /\.(pdf|jpg|jpeg)$/i.test(f.name),
    );
    if (first) {
      setFile(first);
      setUploadError('');
    } else {
      setUploadError('Please select a PDF, JPG, or JPEG file');
      setTimeout(() => setUploadError(''), 3000);
    }
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    pickFile(e.dataTransfer.files);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) pickFile(e.target.files);
    e.target.value = '';
  };

  const canScan = !uploadLoading && !insufficientBalance && !!selectedPolicy &&
    (uploadTab === 'local' ? !!file : !!selectedAmsFile);

  /* Scan API */
  const onStartScan = useCallback(async () => {
    if (!selectedClient || !selectedPolicy) return;
    const isAms = uploadTab === 'ams';
    if (!isAms && !file) return;
    if (isAms && !selectedAmsFile) return;

    setUploadLoading(true);
    setUploadError('');
    try {
      const folderId = isAms ? (selectedAmsFile?.folderId || '') : '';
      const fileIds  = isAms ? [selectedAmsFile?.file.id  || ''] : [];

      const data = new FormData();
      if (!isAms && file) data.append('Invoice', file);
      data.append('ncToken',             getNcToken());
      data.append('insuredId',           selectedClient.databaseId);
      data.append('isFileUploadFromAMS', String(isAms));
      data.append('folderId',            folderId);
      data.append('selectedOption', JSON.stringify({
        useExistingInsured:     true,
        insuredClientId:        selectedClient.databaseId,
        clientName:             selectedClient.name,
        policyNumber:           selectedPolicy.policyNumber,
        selectedPolicyId:       selectedPolicy.id,
        folderId,
        fileIds,
        isFileUploadFromAMSParam: isAms,
      }));

      const res = await axios.post(`${(process.env.REACT_APP_PolicyAgent_Url     || '')}/scan_invoice`, data, {
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      });

      if (res.data.success === true) {
        const raw = typeof res.data.invoiceResponse === 'string'
          ? JSON.parse(res.data.invoiceResponse)
          : res.data.invoiceResponse || res.data;

        const extracted: InvoiceData = {
          invoiceNumber:        raw.invoiceNumber        || '',
          invoiceDate:          raw.invoiceDate          || '',
          invoiceDueDate:       raw.invoiceDueDate       || '',
          billedToName:         raw.billedToName         || raw.billedTo?.name        || '',
          billedToAddressLine:  raw.billedToAddressLine  || raw.billedTo?.addressLine  || '',
          billedToCity:         raw.billedToCity         || raw.billedTo?.city         || '',
          billedToState:        raw.billedToState        || raw.billedTo?.state        || '',
          billedToZipCode:      raw.billedToZipCode      || raw.billedTo?.zipCode      || '',
          soldToName:           raw.soldToName           || raw.soldTo?.name           || '',
          soldToAddressLine:    raw.soldToAddressLine    || raw.soldTo?.addressLine    || '',
          soldToCity:           raw.soldToCity           || raw.soldTo?.city           || '',
          soldToState:          raw.soldToState          || raw.soldTo?.state          || '',
          soldToZipCode:        raw.soldToZipCode        || raw.soldTo?.zipCode        || '',
          paymentAddressLine:   raw.paymentAddressLine   || raw.paymentAddress?.addressLine || '',
          paymentCity:          raw.paymentCity          || raw.paymentAddress?.city   || '',
          paymentState:         raw.paymentState         || raw.paymentAddress?.state  || '',
          paymentZipCode:       raw.paymentZipCode       || raw.paymentAddress?.zipCode || '',
          producer:             raw.producer             || '',
          bookLocation:         raw.bookLocation         || '',
          insuredName:          raw.insuredName          || '',
          policyNumber:         raw.policyNumber         || selectedPolicy.policyNumber,
          insuranceCompany:     raw.insuranceCompany     || '',
          totalAmountDue:       raw.totalAmountDue       || '',
          totalGrossAmount:     raw.totalGrossAmount     || '',
          totalBrokerCommission:raw.totalBrokerCommission|| '',
          invoiceLineItems:     Array.isArray(raw.invoiceLineItems) ? raw.invoiceLineItems : [],
        };

        setInvoiceData(extracted);
        setLineItems(extracted.invoiceLineItems);
        setTransactionId(raw.transactionId || res.data.transactionId || '');
        setFileUrl(raw.fileUrl || res.data.fileUrl || '');
        setIsUpdateInNowcerts(!!(raw.isUpdateInNowcerts || res.data.isUpdateInNowcerts));
        setSelectedBillingType(raw.selectedBillingType || '');
        setBillingTypeAmount(raw.billingTypeAmount || '');
        setStep('review');
      } else {
        setUploadError(res.data.errorMessage || 'Scan failed. Please try again.');
      }
    } catch (err: any) {
      setUploadError(err.response?.data?.errorMessage || 'Error while scanning invoice.');
    } finally {
      setUploadLoading(false);
    }
  }, [file, selectedAmsFile, uploadTab, selectedClient, selectedPolicy]);

  /* Update history after endorsement fee insertion */
  const updateHistory = useCallback(async () => {
    if (!selectedClient || !selectedPolicy || !invoiceData) return;
    await axios.post(
      `${(process.env.REACT_APP_DataFabric_Url      || '')}/api/NCAIInvoiceScan/updatehistory`,
      {
        TransactionId:        transactionId,
        submittedRequestJson: JSON.stringify({ invoiceData, lineItems }),
        selectionOptionsJson: JSON.stringify({
          clientName:      selectedClient.name,
          insuredClientId: selectedClient.databaseId,
          policyNumber:    selectedPolicy.policyNumber,
        }),
      },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    );
  }, [selectedClient, selectedPolicy, invoiceData, lineItems, transactionId]);

  /* Build endorsement fee payload from all line items + header additional fee */
  const getPolicyEndorsementFeeMappingData = useCallback(() => {
    if (!selectedPolicy) return [];
    const fees: any[] = [];
    const parseAmount = (val: string) =>
      parseFloat((val || '').replace('$', '').replace(/,/g, '').replace('(', '').replace(')', '')) || 0;

    // Premium items first (type 0)
    lineItems.forEach(item => {
      if (item.transactionCodeAfterMapped === 'Premium') {
        const amount = parseAmount(item.grossAmount) || parseAmount(item.lineAmount);
        fees.push({ order: 0, policyId: selectedPolicy.id, amount, date: new Date().toJSON(), premiumType: 0, type: 0, followUpDate: new Date().toJSON() });
      }
    });

    // Non-premium items — match against BILLING_TYPE_DATA
    lineItems.forEach(item => {
      if (item.transactionCodeAfterMapped === 'Premium') return;
      const amount = parseAmount(item.grossAmount) || parseAmount(item.lineAmount);
      const matched = BILLING_TYPE_DATA.find(b => b.name === item.transactionCodeAfterMapped);
      if (matched) {
        fees.push({ order: 0, policyId: selectedPolicy.id, amount, date: new Date().toJSON(), premiumType: 1, type: matched.value, followUpDate: new Date().toJSON() });
      }
    });

    // Additional fee/tax from header fields
    if (selectedBillingType && billingTypeAmount) {
      const matched = BILLING_TYPE_DATA.find(b => b.name === selectedBillingType);
      if (matched) {
        fees.push({ order: 0, policyId: selectedPolicy.id, amount: parseFloat(billingTypeAmount) || 0, date: new Date().toJSON(), premiumType: 1, type: matched.value, followUpDate: new Date().toJSON() });
      }
    }

    return fees;
  }, [selectedPolicy, lineItems, selectedBillingType, billingTypeAmount]);

  /* Add endorsement fee — sends all line items in one call */
  const isNumeric = (val: string) => /^-?\d+(\.\d+)?$/.test(val.trim().replace(/[$,]/g, ''));
  const MAX_AMOUNT = 9_999_999.99;
  const fmtAmountInput = (val: string): string => {
    const stripped = val.replace(/[^0-9.]/g, '');
    const parts = stripped.split('.');
    return parts.length > 1 ? parts[0] + '.' + parts.slice(1).join('').substring(0, 2) : parts[0];
  };

  const validateFields = (): boolean => {
    const errors: typeof fieldErrors = {};
    if (invoiceData?.totalAmountDue?.trim()) {
      if (!isNumeric(invoiceData.totalAmountDue)) errors.totalAmountDue = 'Must be a valid number';
      else if (parseFloat(invoiceData.totalAmountDue) > MAX_AMOUNT) errors.totalAmountDue = 'Amount cannot exceed $9,999,999.99';
    }
    if (invoiceData?.totalGrossAmount?.trim()) {
      if (!isNumeric(invoiceData.totalGrossAmount)) errors.totalGrossAmount = 'Must be a valid number';
      else if (parseFloat(invoiceData.totalGrossAmount) > MAX_AMOUNT) errors.totalGrossAmount = 'Amount cannot exceed $9,999,999.99';
    }
    if (selectedBillingType && !billingTypeAmount.trim())
      errors.billingTypeAmount = 'Amount is required when a fee/tax type is selected';
    else if (billingTypeAmount.trim() && !isNumeric(billingTypeAmount))
      errors.billingTypeAmount = 'Must be a valid number';
    else if (billingTypeAmount.trim() && parseFloat(billingTypeAmount) > MAX_AMOUNT)
      errors.billingTypeAmount = 'Amount cannot exceed $9,999,999.99';
    if (billingTypeAmount.trim() && !selectedBillingType)
      errors.selectedBillingType = 'Fee/Tax type is required when an amount is entered';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onAddEndorsementFee = useCallback(async () => {
    if (!selectedClient || !selectedPolicy || !invoiceData) return;
    if (!validateFields()) return;
    const policyEndorsementFee = getPolicyEndorsementFeeMappingData();
    if (!policyEndorsementFee.length) return;

    setFeeLoading(true);
    setFeeError('');

    const ncToken = getNcToken();
    try {
      await axios({
        method: 'POST',
        url: `${(process.env.REACT_APP_NOWCERTSAPI         || '')}/api/nowcert/insertpolicyendorsementfee?ncToken=${ncToken}`,
        headers: {
          Authorization:  `Bearer ${getToolboxToken()}`,
          'Content-Type': 'application/json',
        },
        data: JSON.stringify(policyEndorsementFee),
      });

      await updateHistory();
      setFeeSuccess(true);
    } catch (err: any) {
      setFeeError(err.response?.data?.errorMessage || 'Error adding endorsement fee.');
    } finally {
      setFeeLoading(false);
    }
  }, [selectedClient, selectedPolicy, invoiceData, getPolicyEndorsementFeeMappingData, updateHistory]);

  const resetAll = useCallback(() => {
    setStep('client-select');
    setClientQuery('');
    setClientResults([]);
    setSelectedClient(null);
    setPolicyList([]);
    setSelectedPolicy(null);
    setUploadTab('local');
    setFile(null);
    setSelectedAmsFile(null);
    setIsDragging(false);
    setUploadLoading(false);
    setUploadError('');
    setInvoiceData(null);
    setLineItems([]);
    setTransactionId('');
    setFileUrl('');
    setIsUpdateInNowcerts(false);
    setSelectedBillingType('');
    setBillingTypeAmount('');
    setLineItemsPage(1);
    setFeeLoading(false);
    setFeeSuccess(false);
    setFeeError('');
  }, []);

  const updateLineItem = (index: number, field: keyof LineItem, value: string) => {
    setLineItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  /* ── STEP 1: Client + Policy selection ── */
  if (step === 'client-select') {
    return (
      <div className="cs-main" style={{ padding: 0, gap: 14 }}>
        <div className="cs-client-search">
          <div className="cs-client-search__input-wrap">
            <FiSearch size={15} className="cs-client-search__icon" />
            <input
              className="cs-client-search__input"
              type="text"
              placeholder="Search client name…"
              value={clientQuery}
              onChange={e => { setClientQuery(e.target.value); if (selectedClient) setSelectedClient(null); }}
              autoFocus
            />
            {clientLoading && <span className="cs-client-search__spinner" />}
          </div>

          {clientResults.length > 0 && (
            <ul className="cs-client-dropdown">
              {clientResults.map(c => (
                <li key={c.databaseId} className="cs-client-dropdown__item" onClick={() => selectClient(c)}>
                  <span className="cs-client-dropdown__name">{c.name}</span>
                  {c.eMail && <span className="cs-client-dropdown__email">{c.eMail}</span>}
                </li>
              ))}
            </ul>
          )}
          {clientQuery.trim() && !clientLoading && clientResults.length === 0 && (
            <ul className="cs-client-dropdown">
              <li className="cs-client-dropdown__item" style={{ cursor: 'default', color: 'var(--text-secondary)', opacity: 0.8 }}>No clients found</li>
            </ul>
          )}
        </div>

        {/* Policy table — shown after client selected */}
        {selectedClient && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 10 }}>
              Client: <span style={{ color: 'var(--text-primary)' }}>{selectedClient.name}</span>
              <span style={{ margin: '0 6px', color: 'var(--border-glass)' }}>·</span>
              Select a policy to continue
            </div>

            {policyLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 0', color: 'var(--text-secondary)', fontSize: 13 }}>
                <span className="cs-client-search__spinner" /> Loading policies…
              </div>
            ) : policyList.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-tertiary)', padding: '8px 0' }}>No policies found for this client.</div>
            ) : (
              <div style={{
                border: '1px solid var(--border-glass)', borderRadius: 8,
                overflow: 'hidden', maxHeight: 280, overflowY: 'auto',
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--surface-glass)', borderBottom: '1px solid var(--border-glass)' }}>
                      <th style={{ width: 44, padding: '9px 12px', textAlign: 'center', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11 }}></th>
                      <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11 }}>Title</th>
                      <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11, whiteSpace: 'nowrap' }}>Effective Date</th>
                      <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11, whiteSpace: 'nowrap' }}>Policy Number</th>
                      <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11, whiteSpace: 'nowrap' }}>Total Premium</th>
                    </tr>
                  </thead>
                  <tbody>
                    {policyList.map((p, idx) => {
                      const isSelected = selectedPolicy?.id === p.id;
                      return (
                        <tr
                          key={p.id}
                          onClick={() => setSelectedPolicy(p)}
                          style={{
                            cursor: 'pointer',
                            background: isSelected ? 'rgba(124,58,237,0.08)' : idx % 2 === 0 ? 'transparent' : 'var(--surface-glass)',
                            borderBottom: '1px solid var(--border-glass)',
                            transition: 'background 0.12s',
                          }}
                        >
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            <div style={{
                              width: 20, height: 20, borderRadius: '50%', margin: '0 auto',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              border: `2px solid ${isSelected ? '#7c3aed' : 'var(--border-glass)'}`,
                              background: isSelected ? '#7c3aed' : 'transparent',
                              flexShrink: 0, transition: 'all 0.15s',
                            }}>
                              {isSelected && (
                                <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                                  <path d="M1 4L4 7.5L10 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '10px 12px', color: isSelected ? 'var(--accent-purple, #7c3aed)' : 'var(--text-primary)', fontWeight: isSelected ? 600 : 400 }}>
                            {p.linesOfBusiness?.length ? p.linesOfBusiness.join(', ') : '—'}
                          </td>
                          <td style={{ padding: '10px 12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                            {p.effectiveDate ? new Date(p.effectiveDate).toLocaleDateString() : '—'}
                          </td>
                          <td style={{ padding: '10px 12px', color: isSelected ? 'var(--accent-purple, #7c3aed)' : 'var(--text-primary)', fontWeight: isSelected ? 600 : 400, whiteSpace: 'nowrap' }}>
                            {p.policyNumber || '—'}
                          </td>
                          <td style={{ padding: '10px 12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                            {p.totalPremium != null ? `$${Number(p.totalPremium).toLocaleString()}` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {selectedPolicy && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                <motion.button
                  className="cs-btn-start"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setStep('upload')}
                >
                  Continue →
                </motion.button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  /* ── STEP 2: File Upload ── */
  if (step === 'upload') {
    return (
      <div className="cs-main" style={{ padding: 0, gap: 14 }}>
        {/* Top row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            className="cs-btn-cancel"
            onClick={() => setStep('client-select')}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <FiChevronLeft size={13} /> Back
          </button>
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
            {selectedClient?.name} — Policy: <strong>{selectedPolicy?.policyNumber}</strong>
          </span>
        </div>

        {/* Balance bar */}
        {/* <div className="coi-balance-bar">
          {balanceLoading
            ? <span className="cs-upload-spinner" />
            : <><span>Balance: ${balance.toFixed(2)}</span><span style={{ opacity: 0.6 }}>· 25¢ per scan</span></>
          }
        </div> */}

        {insufficientBalance && (
          <div className="coi-insufficient-banner">
            <FiAlertCircle size={14} />
            Insufficient balance. Please add credits to continue scanning.
          </div>
        )}

        {/* Tabs */}
        <div className="cs-tabs">
          <button
            className={`cs-tab${uploadTab === 'local' ? ' cs-tab--active' : ''}`}
            onClick={() => { setUploadTab('local'); setSelectedAmsFile(null); }}
          >
            Select Document from Local Drive
          </button>
          <button
            className={`cs-tab${uploadTab === 'ams' ? ' cs-tab--active' : ''}`}
            onClick={() => { setUploadTab('ams'); setFile(null); }}
          >
            Select Document from Client Profile
          </button>
        </div>

        {/* Content */}
        <div className="cs-content">
          {uploadTab === 'local' ? (
            <div
              className={`cs-dropzone${isDragging ? ' cs-dropzone--dragging' : ''}`}
              onDrop={handleDrop}
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
            >
              {!file ? (
                <>
                  <div className="cs-dropzone__icon"><FiFileText size={28} /></div>
                  <p className="cs-dropzone__title">Drag & Drop your invoice here</p>
                  <p className="cs-dropzone__or">or</p>
                  <motion.button
                    className="cs-browse-btn"
                    onClick={() => fileInputRef.current?.click()}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    Browse Files
                  </motion.button>
                  <p className="cs-dropzone__types">Allowed: PDF, JPG, JPEG</p>
                </>
              ) : (
                <div className="cs-file-card">
                  <div className="cs-file-card__icon"><FiFileText size={20} /></div>
                  <div className="cs-file-card__info">
                    <span className="cs-file-card__name">{file.name}</span>
                    <span className="cs-file-card__size">{(file.size / 1024).toFixed(0)} KB</span>
                  </div>
                  <button className="cs-file-card__delete" onClick={() => setFile(null)} title="Remove">
                    <FiTrash2 size={15} />
                  </button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                style={{ display: 'none' }}
                onChange={handleFileInput}
              />
            </div>
          ) : (
            <AmsFileBrowser
              insuredId={selectedClient!.databaseId}
              clientName={selectedClient!.name}
              onFileSelected={setSelectedAmsFile}
            />
          )}
        </div>

        <div className="cs-info-banner">
          <FiAlertCircle size={14} />
          <span>AI extracts invoice fields automatically. Supported: <strong>PDF, JPG, JPEG</strong></span>
        </div>

        {uploadError && (
          <div style={{ color: '#dc2626', fontSize: 12, padding: '4px 0' }}>{uploadError}</div>
        )}

        {/* Footer */}
        <div className="cs-footer">
          <span className="cs-footer__status">
            {uploadTab === 'local'
              ? (!file ? 'No file added yet.' : `${file.name} ready to scan`)
              : (!selectedAmsFile ? 'No file selected.' : `${selectedAmsFile.file.name} ready to scan`)}
          </span>
          <div className="cs-footer__actions">
            <button className="cs-btn-cancel" onClick={resetAll} disabled={uploadLoading}>Cancel</button>
            <motion.button
              className="cs-btn-start"
              disabled={!canScan}
              whileHover={{ scale: canScan ? 1.03 : 1 }}
              whileTap={{ scale: canScan ? 0.97 : 1 }}
              onClick={onStartScan}
            >
              {uploadLoading ? <span className="cs-upload-spinner" /> : '✦ Start Scan'}
            </motion.button>
          </div>
        </div>
      </div>
    );
  }

  /* ── STEP 3: Review extracted invoice data ── */
  if (!invoiceData) return null;

  const isReadOnly = isUpdateInNowcerts;

  const toDateInput = (val: string): string => {
    if (!val) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    const dmy = val.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (dmy) return `${dmy[3]}-${dmy[2].padStart(2,'0')}-${dmy[1].padStart(2,'0')}`;
    const mdy = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdy) return `${mdy[3]}-${mdy[1].padStart(2,'0')}-${mdy[2].padStart(2,'0')}`;
    return '';
  };

  const ctrlStyle = (editable: boolean): React.CSSProperties => ({
    width: '100%', padding: '7px 10px', borderRadius: 6, fontSize: 12,
    background: editable ? 'var(--surface-glass)' : 'rgba(255,255,255,0.03)',
    border: `1px solid ${editable ? 'var(--border-glass)' : 'rgba(255,255,255,0.06)'}`,
    color: editable ? 'var(--text-primary)' : 'var(--text-secondary)',
    outline: 'none', boxSizing: 'border-box' as const,
    cursor: editable ? 'text' : 'default',
  });

  const headerRows: Array<Array<{ label: string; node: React.ReactNode }>> = [
    [
      { label: 'Invoice Number', node: (
        <input type="text" value={invoiceData.invoiceNumber} readOnly={isReadOnly} placeholder="Enter Invoice Number"
          onChange={e => !isReadOnly && setInvoiceData(p => p ? { ...p, invoiceNumber: e.target.value } : p)}
          style={ctrlStyle(!isReadOnly)} />
      )},
      { label: 'Invoice Date', node: (
        <input type="date" value={toDateInput(invoiceData.invoiceDate)} readOnly={isReadOnly}
          onChange={e => !isReadOnly && setInvoiceData(p => p ? { ...p, invoiceDate: e.target.value } : p)}
          style={ctrlStyle(!isReadOnly)} />
      )},
      { label: 'Invoice Due Date', node: (
        <input type="date" value={toDateInput(invoiceData.invoiceDueDate)} readOnly={isReadOnly}
          onChange={e => !isReadOnly && setInvoiceData(p => p ? { ...p, invoiceDueDate: e.target.value } : p)}
          style={ctrlStyle(!isReadOnly)} />
      )},
    ],
    [
      { label: 'Total Amount Due ($)', node: (
        <>
          <input type="text" value={(invoiceData.totalAmountDue || '').replace(/[$,]/g, '')} readOnly={isReadOnly} placeholder="Enter Total Amount Due"
            onChange={e => { if (!isReadOnly) { const v = fmtAmountInput(e.target.value); setInvoiceData(p => p ? { ...p, totalAmountDue: v } : p); setFieldErrors(prev => ({ ...prev, totalAmountDue: undefined })); } }}
            style={{ ...ctrlStyle(!isReadOnly), ...(fieldErrors.totalAmountDue ? { borderColor: '#ef4444' } : {}) }} />
          {fieldErrors.totalAmountDue && <span style={{ fontSize: 11, color: '#ef4444', marginTop: 3, display: 'block' }}>{fieldErrors.totalAmountDue}</span>}
        </>
      )},
      { label: 'Total Gross Amount ($)', node: (
        <>
          <input type="text" value={(invoiceData.totalGrossAmount || '').replace(/[$,]/g, '')} readOnly={isReadOnly} placeholder="Enter Total Gross Amount"
            onChange={e => { if (!isReadOnly) { const v = fmtAmountInput(e.target.value); setInvoiceData(p => p ? { ...p, totalGrossAmount: v } : p); setFieldErrors(prev => ({ ...prev, totalGrossAmount: undefined })); } }}
            style={{ ...ctrlStyle(!isReadOnly), ...(fieldErrors.totalGrossAmount ? { borderColor: '#ef4444' } : {}) }} />
          {fieldErrors.totalGrossAmount && <span style={{ fontSize: 11, color: '#ef4444', marginTop: 3, display: 'block' }}>{fieldErrors.totalGrossAmount}</span>}
        </>
      )},
      { label: 'Insured Name', node: (
        <input type="text" value={invoiceData.insuredName} readOnly={isReadOnly} placeholder="Enter Insured Name"
          onChange={e => !isReadOnly && setInvoiceData(p => p ? { ...p, insuredName: e.target.value } : p)}
          style={ctrlStyle(!isReadOnly)} />
      )},
    ],
    [
      { label: 'Policy Number', node: (
        <input type="text" value={invoiceData.policyNumber} readOnly={isReadOnly} placeholder="Enter Policy Number"
          onChange={e => !isReadOnly && setInvoiceData(p => p ? { ...p, policyNumber: e.target.value } : p)}
          style={ctrlStyle(!isReadOnly)} />
      )},
      { label: 'Additional Fee/Tax Type', node: (
        <div ref={billingDropdownRef} style={{ position: 'relative' }}>
          <button
            type="button"
            disabled={isReadOnly}
            onClick={() => !isReadOnly && setBillingDropdownOpen(o => !o)}
            style={{ ...ctrlStyle(!isReadOnly), cursor: isReadOnly ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', textAlign: 'left' }}
          >
            <span style={{ color: selectedBillingType ? 'var(--text-primary)' : 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedBillingType ? selectedBillingType.replace(/_/g, ' ') : 'Select fee / tax type'}
            </span>
            <span style={{ color: 'var(--text-secondary)', fontSize: 10, flexShrink: 0, marginLeft: 4 }}>▾</span>
          </button>
          {billingDropdownOpen && (
            <div className="ncai-billing-dropdown">
              <div
                className={`ncai-billing-option ncai-billing-option--placeholder${!selectedBillingType ? ' ncai-billing-option--selected' : ''}`}
                onMouseDown={() => { setSelectedBillingType(''); setBillingDropdownOpen(false); setFieldErrors(prev => ({ ...prev, billingTypeAmount: undefined })); }}
              >Select fee / tax type</div>
              {BILLING_TYPE_DATA.map(bt => (
                <div
                  key={bt.name}
                  className={`ncai-billing-option${selectedBillingType === bt.name ? ' ncai-billing-option--selected' : ''}`}
                  onMouseDown={() => { setSelectedBillingType(bt.name); setBillingDropdownOpen(false); setFieldErrors(prev => ({ ...prev, selectedBillingType: undefined })); }}
                >{bt.name.replace(/_/g, ' ')}</div>
              ))}
            </div>
          )}
          {fieldErrors.selectedBillingType && <span style={{ fontSize: 11, color: '#ef4444', marginTop: 3, display: 'block' }}>{fieldErrors.selectedBillingType}</span>}
        </div>
      )},
      { label: 'Amount', node: (
        <>
        <input type="text" value={billingTypeAmount} readOnly={isReadOnly} placeholder="Enter Amount"
          onChange={e => { if (!isReadOnly) { const v = fmtAmountInput(e.target.value); setBillingTypeAmount(v); setFieldErrors(prev => ({ ...prev, billingTypeAmount: undefined, selectedBillingType: undefined })); } }}
          style={{ ...ctrlStyle(!isReadOnly), ...(fieldErrors.billingTypeAmount ? { borderColor: '#ef4444' } : {}) }} />
        {fieldErrors.billingTypeAmount && <span style={{ fontSize: 11, color: '#ef4444', marginTop: 3, display: 'block' }}>{fieldErrors.billingTypeAmount}</span>}
        </>
      )},
    ],
  ];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
      {/* Top bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px',
        borderBottom: '1px solid var(--border-glass)', flexShrink: 0,
        background: 'var(--surface-glass)',
      }}>
        <button
          onClick={resetAll}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600,
            padding: '4px 6px', borderRadius: 6,
          }}
        >
          ← Scan Again
        </button>
        <span style={{ color: 'var(--border-glass)', fontSize: 16 }}>|</span>
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
          {selectedClient?.name}
          <span style={{ color: 'var(--text-secondary)', margin: '0 6px' }}>·</span>
          Policy: <strong style={{ color: 'var(--accent-purple)' }}>{selectedPolicy?.policyNumber}</strong>
        </span>
      </div>

      {/* Body: left doc + right fields */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>

        {/* Left: document preview */}
        <div style={{
          width: 400, flexShrink: 0, display: 'flex', flexDirection: 'column',
          borderRight: '1px solid var(--border-glass)', background: '#111',
        }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af' }}>Uploaded Invoice</span>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {fileUrl
              ? <iframe src={`${fileUrl}`} title="Invoice Preview" style={{ width: '100%', height: '100%', border: 'none' }} />
              : <div className="cspr-pdf-placeholder">Preview not available</div>
            }
          </div>
        </div>

        {/* Right: data panel */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)', overflow: 'hidden' }}>
          <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Invoice header card */}
            <div style={{ background: 'var(--surface-glass)', border: '1px solid var(--border-glass)', borderRadius: 10, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Invoice Details</span>
                {isReadOnly && (
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#10b981', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <FiCheck size={12} /> Already in NowCerts
                  </span>
                )}
              </div>
              {headerRows.map((row, ri) => (
                <div key={ri} style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '0 14px' }}>
                  {row.map(({ label, node }) => (
                    <div key={label}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 5, letterSpacing: '0.01em' }}>
                        {label}
                      </div>
                      {node}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Line items table */}
            {(() => {
              const LI_PAGE_SIZE = 10;
              const liTotalPages = Math.max(1, Math.ceil(lineItems.length / LI_PAGE_SIZE));
              const liPage       = Math.min(lineItemsPage, liTotalPages);
              const liSlice      = lineItems.slice((liPage - 1) * LI_PAGE_SIZE, liPage * LI_PAGE_SIZE);
              const liPageList   = Array.from({ length: liTotalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === liTotalPages || Math.abs(p - liPage) <= 1)
                .reduce<(number | '…')[]>((acc, p, idx, arr) => {
                  if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, []);

              return (
                <>
                <div style={{ background: 'var(--surface-glass)', border: '1px solid var(--border-glass)', borderRadius: 10, overflow: 'hidden' }}>
                  {/* Header */}
                  <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border-glass)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Line Items ({lineItems.length})</span>
                    {lineItems.length > 0 && (
                      <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                        {(liPage - 1) * LI_PAGE_SIZE + 1}–{Math.min(liPage * LI_PAGE_SIZE, lineItems.length)} of {lineItems.length}
                      </span>
                    )}
                  </div>

                  {lineItems.length === 0 ? (
                    <div style={{ padding: '24px 20px', color: 'var(--text-secondary)', fontSize: 13, textAlign: 'center' }}>
                      No line items extracted.
                    </div>
                  ) : (
                    <>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid var(--border-glass)' }}>
                              {['Line Code', 'Transaction Code', 'Billing Type', 'Effective Date', 'Gross Amount', 'Broker Commission', 'Line Amount'].map(col => (
                                <th key={col} style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap', fontSize: 11, letterSpacing: '0.02em' }}>
                                  {col}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {liSlice.map((item, i) => {
                              const idx = (liPage - 1) * LI_PAGE_SIZE + i;
                              return (
                                <tr key={idx} style={{ borderBottom: '1px solid var(--border-glass)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
                                  <td style={{ padding: '9px 12px', color: 'var(--accent-purple)', fontWeight: 500, whiteSpace: 'nowrap' }}>{item.lineCode || '—'}</td>
                                  <td style={{ padding: '9px 12px', color: 'var(--text-primary)', maxWidth: 180, wordBreak: 'break-word' }}>{item.transactionCodeBeforeMapped || '—'}</td>
                                  <td style={{ padding: '9px 12px', color: 'var(--text-secondary)' }}>{item.transactionCodeAfterMapped || 'Other'}</td>
                                  <td style={{ padding: '9px 12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{item.effectiveDate || '—'}</td>
                                  <td style={{ padding: '9px 12px', color: 'var(--text-primary)', fontWeight: 500, whiteSpace: 'nowrap' }}>{item.grossAmount || '—'}</td>
                                  <td style={{ padding: '9px 12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{item.brokerCommission || '—'}</td>
                                  <td style={{ padding: '9px 12px', color: 'var(--accent-purple)', fontWeight: 600, whiteSpace: 'nowrap' }}>{item.lineAmount || '—'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {liTotalPages > 1 && (
                        <div className="qp-pagination" style={{ borderTop: '1px solid var(--border-glass)', padding: '8px 14px', justifyContent: 'flex-end' }}>
                          <div className="qp-pagination__controls">
                            <button className="qp-pagination__btn" disabled={liPage === 1} onClick={() => setLineItemsPage(1)}>«</button>
                            <button className="qp-pagination__btn" disabled={liPage === 1} onClick={() => setLineItemsPage(p => p - 1)}>‹</button>
                            {liPageList.map((p, i) =>
                              p === '…'
                                ? <span key={`e-${i}`} className="qp-pagination__ellipsis">…</span>
                                : <button
                                    key={p}
                                    className={`qp-pagination__btn${liPage === p ? ' qp-pagination__btn--active' : ''}`}
                                    onClick={() => setLineItemsPage(p as number)}
                                  >{p}</button>
                            )}
                            <button className="qp-pagination__btn" disabled={liPage === liTotalPages} onClick={() => setLineItemsPage(p => p + 1)}>›</button>
                            <button className="qp-pagination__btn" disabled={liPage === liTotalPages} onClick={() => setLineItemsPage(liTotalPages)}>»</button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Add Endorsement Fees — below line items, right-aligned, hidden when already in NowCerts */}
                {!isReadOnly && lineItems.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, paddingTop: 4 }}>
                    {feeError && <span style={{ fontSize: 11, color: '#ef4444' }}>{feeError}</span>}
                    {feeSuccess ? (
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#10b981', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <FiCheck size={13} /> Endorsement fees added successfully
                      </span>
                    ) : (
                      <motion.button
                        className="cspr-action-btn cspr-action-btn--purple"
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        disabled={feeLoading}
                        onClick={onAddEndorsementFee}
                        style={{ fontSize: 12, padding: '8px 22px', opacity: feeLoading ? 0.6 : 1 }}
                      >
                        {feeLoading ? 'Adding…' : 'Add Endorsement Fees'}
                      </motion.button>
                    )}
                  </div>
                )}
                </>
              );
            })()}

          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceScanUpload;

